/**
 * EntitlementContext — what the signed-in user's plan unlocks, and the one upgrade prompt for the app.
 *
 *   const { has, requireFeature } = useEntitlements();
 *   <button onClick={() => requireFeature('quiz') && openQuiz()}>
 *
 * - `has(key)` is true for staff, when gating is switched off, while entitlements are still loading, and if
 *   they couldn't be loaded (fail-open: a plan lookup hiccup must never lock a paying user out -- the backend
 *   still enforces).
 * - `requireFeature(key)` returns has(key) and, when locked, opens the upgrade prompt.
 * - A backend 403 FEATURE_NOT_IN_PLAN anywhere (client.ts dispatches "feature-locked") also opens it.
 *
 * Mounted inside the router (AppRouter) so the prompt can navigate to /pricing.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import { entitlementService, type Entitlements } from '@services/api/entitlement.service';
import { FEATURE_BY_KEY } from '@/config/featureRegistry';

interface EntitlementContextValue {
  entitlements: Entitlements | null;
  loading: boolean;
  has: (featureKey: string) => boolean;
  /** Announced but not launched yet (always false for staff, who can use it to test). */
  isComingSoon: (featureKey: string) => boolean;
  /** Per-plan config for an included feature (e.g. {max_attempts: 3}); null when not included. */
  config: (featureKey: string) => Record<string, any> | null;
  requireFeature: (featureKey: string) => boolean;
  openUpgrade: (featureKey: string, message?: string) => void;
  refresh: () => Promise<void>;
  trialDaysLeft: number | null;
}

const EntitlementContext = createContext<EntitlementContextValue | null>(null);

export const EntitlementProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState<{ feature: string; message?: string } | null>(null);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) { setEntitlements(null); return; }
    setLoading(true);
    try {
      const mine = await entitlementService.getMine();
      // Anything that is not an entitlements object (an empty list from a proxy or stub, a half-written response)
      // is treated like a failed load: fail open below instead of crashing every protected page on `.features`.
      setEntitlements(mine && typeof mine === 'object' && !Array.isArray(mine) && mine.features ? mine : null);
    } catch (err) {
      console.warn('[Entitlements] could not load -- failing open (backend still enforces)', err);
      setEntitlements(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => { refresh(); }, [refresh, user?.id]);

  const isComingSoon = useCallback(
    (key: string) => !!entitlements && entitlements.source !== 'staff' && (entitlements.coming_soon || []).includes(key),
    [entitlements],
  );

  const has = useCallback((key: string) => {
    if (!entitlements) return true;
    if (entitlements.source === 'staff') return true;
    // "Coming soon" is a launch switch, not plan gating -- it holds even when gating is off.
    if ((entitlements.coming_soon || []).includes(key)) return false;
    if (entitlements.enforcement === 'off') return true;
    return key in entitlements.features;
  }, [entitlements]);

  const config = useCallback((key: string) => {
    if (!entitlements) return {};
    return entitlements.features[key] ?? (has(key) ? {} : null);
  }, [entitlements, has]);

  const openUpgrade = useCallback((feature: string, message?: string) => setPrompt({ feature, message }), []);

  const requireFeature = useCallback((key: string) => {
    if (has(key)) return true;
    openUpgrade(key);
    return false;
  }, [has, openUpgrade]);

  useEffect(() => {
    const onLocked = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      if (d.feature) openUpgrade(d.feature, d.message);
    };
    window.addEventListener('feature-locked', onLocked);
    return () => window.removeEventListener('feature-locked', onLocked);
  }, [openUpgrade]);

  const trialDaysLeft = useMemo(() => {
    if (entitlements?.source !== 'trial' || !entitlements.trial_ends_at) return null;
    const ms = new Date(entitlements.trial_ends_at).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 86_400_000));
  }, [entitlements]);

  const value = useMemo(
    () => ({ entitlements, loading, has, isComingSoon, config, requireFeature, openUpgrade, refresh, trialDaysLeft }),
    [entitlements, loading, has, isComingSoon, config, requireFeature, openUpgrade, refresh, trialDaysLeft],
  );

  return (
    <EntitlementContext.Provider value={value}>
      {children}
      {prompt && (
        <UpgradeModal
          feature={prompt.feature}
          message={prompt.message}
          entitlements={entitlements}
          comingSoon={isComingSoon(prompt.feature)}
          onClose={() => setPrompt(null)}
        />
      )}
    </EntitlementContext.Provider>
  );
};

export const useEntitlements = (): EntitlementContextValue => {
  const ctx = useContext(EntitlementContext);
  if (ctx) return ctx;
  // Outside the provider (e.g. a public page): everything open, nothing to prompt.
  return {
    entitlements: null, loading: false, has: () => true, isComingSoon: () => false, config: () => ({}), requireFeature: () => true,
    openUpgrade: () => {}, refresh: async () => {}, trialDaysLeft: null,
  };
};

// ─── Upgrade prompt ─────────────────────────────────────────────────────────

const UpgradeModal: React.FC<{
  feature: string;
  message?: string;
  entitlements: Entitlements | null;
  comingSoon?: boolean;
  onClose: () => void;
}> = ({ feature, message, entitlements, comingSoon, onClose }) => {
  const navigate = useNavigate();
  const label = FEATURE_BY_KEY[feature]?.label || 'This feature';
  const hint = entitlements?.upgrade_hints?.[feature];
  const isSchoolOnly = hint?.plan_code === 'school';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (comingSoon) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="soon-title">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-700">
          <div className="h-1.5 bg-gradient-to-r from-violet-500 via-fuchsia-500 to-amber-400" />
          <div className="p-6 text-center">
            <div className="mx-auto mb-3 w-14 h-14 rounded-2xl bg-violet-50 dark:bg-violet-500/15 flex items-center justify-center text-2xl">✨</div>
            <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-violet-600 dark:text-violet-400 mb-1">Coming soon</p>
            <h2 id="soon-title" className="text-lg font-bold text-slate-900 dark:text-white mb-1">{label} is almost here</h2>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-5">
              We're putting the finishing touches on it. It will switch on automatically for your plan the day it launches —
              nothing to do on your side.
            </p>
            <button onClick={onClose} className="px-6 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold">
              Got it
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="upgrade-title">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-700">
        <div className="h-1.5 bg-gradient-to-r from-indigo-500 via-violet-500 to-amber-400" />
        <div className="p-6 text-center">
          <div className="mx-auto mb-3 w-14 h-14 rounded-2xl bg-indigo-50 dark:bg-indigo-500/15 flex items-center justify-center text-2xl">🔒</div>
          <h2 id="upgrade-title" className="text-lg font-bold text-slate-900 dark:text-white mb-1">
            {label} {hint ? <>is part of <span className="text-indigo-600 dark:text-indigo-400">{hint.plan_name}</span></> : 'isn’t in your plan'}
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-5">
            {message || (isSchoolOnly
              ? 'This comes with a School account. Ask your school about it, or book a demo for your institution.'
              : `You're on ${entitlements?.plan_name || 'the Free plan'}. Upgrade to unlock it — and everything else in ${hint?.plan_name || 'a higher plan'}.`)}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              onClick={() => { onClose(); navigate('/pricing'); }}
              className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold"
            >
              {isSchoolOnly ? 'See school plans' : 'See plans & upgrade'}
            </button>
            <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-sm font-semibold">
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EntitlementContext;
